import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from 'components/Seo';
import { InkFooter, InkNav, useInkRoot } from './shared';
import 'styles/ink-feedback.css';

export default function InkFeedbackPage({ type }) {
  useInkRoot();
  const bug = type === 'bug';
  const title = bug ? 'Report a bug' : 'Suggest a feature';
  const [form, setForm] = useState(() => ({ title: '', details: '', steps: '', expected: '', email: '',
    version: (new URLSearchParams(window.location.search).get('version') || '').slice(0,64), os: '', website: '' }));
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());
  const [status, setStatus] = useState({ kind: '', text: '' });
  const [sending, setSending] = useState(false);
  const set = key => event => {
    setForm(current => ({ ...current, [key]: event.target.value }));
    setSubmissionId(crypto.randomUUID());
  };
  const submit = async event => {
    event.preventDefault();
    if (sending) return;
    setSending(true); setStatus({ kind: '', text: '' });
    try {
      const response = await fetch('/api/ink/feedback', { method: 'POST', signal: AbortSignal.timeout(20000),
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, type, submissionId }) });
      const result = await response.json();
      if (!response.ok || !result.ok || !result.reference) throw new Error(String(response.status));
      setStatus({ kind: 'success', text: result.reference });
    } catch (error) {
      setStatus({ kind: 'error', text: error.message === '429'
        ? 'You have sent several reports recently. Please try again later, or email ink-support@neevii.com.'
        : 'Your message could not be sent. Your text is still here—please try again, or email ink-support@neevii.com.' });
    } finally { setSending(false); }
  };
  return <div className="ink2 ink-feedback-page">
    <Seo title={`${title} — Neevii Ink`} description={bug ? 'Help improve Neevii Ink by reporting a problem.' : 'Share an idea for improving your Neevii Ink workflow.'} schema={false} />
    <InkNav />
    <main className="wrap feedback-main">
      <div className="feedback-heading">
        <Link to="/ink" className="feedback-back">← Neevii Ink</Link>
        <p className="feedback-eyebrow">Help shape Neevii Ink</p>
        <h1>{title}</h1>
        <p className="lead">{bug ? 'Tell us what happened and how to reproduce it.' : 'Tell us what you would like to do and how it would help your work.'}</p>
        <nav className="feedback-tabs" aria-label="Feedback type">
          <Link to={`/ink/report-bug?version=${encodeURIComponent(form.version)}`} aria-current={bug ? 'page' : undefined}>Report a bug</Link>
          <Link to={`/ink/suggest-feature?version=${encodeURIComponent(form.version)}`} aria-current={!bug ? 'page' : undefined}>Suggest a feature</Link>
        </nav>
      </div>
      {status.kind === 'success' ? <section className="feedback-card feedback-success" role="status">
        <h2>Thank you. Your {bug ? 'bug report' : 'feature request'} has been sent.</h2>
        <p>Your reference is <strong>{status.text}</strong>. Keep it if you contact us about this message.</p>
        <p>If you supplied an email address, we can reply when we need more information.</p>
        <button className="btn btn-p" type="button" onClick={() => {
          setForm(current => ({ ...current, title: '', details: '', steps: '', expected: '' }));
          setSubmissionId(crypto.randomUUID()); setStatus({ kind: '', text: '' });
        }}>Send another {bug ? 'report' : 'suggestion'}</button>
      </section> : <form className="feedback-card" onSubmit={submit}>
        <label>Short title <span>(required)</span><input name="title" value={form.title} onChange={set('title')} maxLength={160} required
          placeholder={bug ? 'For example: Tile puts documents outside the workspace' : 'For example: Add a shortcut for loading a mask selection'} /></label>
        <label>{bug ? 'What went wrong?' : 'What would you like to do?'} <span>(required)</span>
          <textarea name="details" value={form.details} onChange={set('details')} maxLength={6000} rows={5} required
            placeholder={bug ? 'Describe the problem you encountered.' : 'Describe your idea and the workflow it would help with.'} /></label>
        {bug && <>
          <label>Steps to reproduce <span>(required)</span><textarea name="steps" value={form.steps} onChange={set('steps')} maxLength={4000} rows={4} required placeholder={'1. Open an image\n2. Choose…\n3. The problem happens when…'} /></label>
          <label>What did you expect to happen? <span>(optional)</span><textarea name="expected" value={form.expected} onChange={set('expected')} maxLength={2000} rows={3} /></label>
        </>}
        <div className="feedback-row">
          <label>App version <span>(optional)</span><input name="version" value={form.version} onChange={set('version')} maxLength={64} placeholder="Shown in Help" /></label>
          <label>Operating system <span>(optional)</span><input name="os" value={form.os} onChange={set('os')} maxLength={120} placeholder="For example: Windows 11 or Ubuntu 24.04" /></label>
        </div>
        <label>Email <span>(optional, so we can reply)</span><input name="email" type="email" autoComplete="email" value={form.email} onChange={set('email')} maxLength={200} /></label>
        <div className="feedback-honeypot" aria-hidden="true"><label>Website<input name="website" value={form.website} onChange={set('website')} tabIndex={-1} autoComplete="off" /></label></div>
        <p className="feedback-note">This message goes to ink-support@neevii.com. Do not include confidential production material. No documents or logs are attached automatically. <Link to="/privacy">Privacy policy</Link></p>
        {status.kind === 'error' && <p className="feedback-error" role="alert">{status.text} <a href="mailto:ink-support@neevii.com">Open email</a></p>}
        <button type="submit" className="btn btn-p" disabled={sending}>{sending ? 'Sending…' : bug ? 'Send bug report' : 'Send feature request'}</button>
      </form>}
    </main>
    <InkFooter />
  </div>;
}
